import {
  authUserSchema,
  type AuthResponse,
  type LoginRequest,
  type RegisterRequest,
} from '@libellus/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, onUnauthorized } from '../../lib/api';
import { loadPreference, removePreference, savePreference } from '../../lib/preferences';
import { tokenStore } from '../../lib/token-store';
import { authApi } from './api';
import { SessionContext, type Session } from './session-context';

/** The signed-in user, remembered so the app opens signed in without a connection. */
const USER_KEY = 'libellus.user';

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session>({ status: 'loading' });

  const signOut = useCallback(async () => {
    await Promise.all([tokenStore.clear(), removePreference(USER_KEY)]);
    queryClient.clear();
    setSession({ status: 'signedOut' });
  }, [queryClient]);

  // Restore a saved session on launch. A remembered user opens the app straight away; the
  // token is confirmed in the background, and only a rejected token ends the session (being
  // offline or a server hiccup does not).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [token, remembered] = await Promise.all([
        tokenStore.load(),
        loadPreference(USER_KEY, authUserSchema.nullable(), null),
      ]);
      if (!token) {
        if (!cancelled) setSession({ status: 'signedOut' });
        return;
      }
      if (remembered && !cancelled) setSession({ status: 'signedIn', user: remembered });

      try {
        const user = await authApi.me();
        await savePreference(USER_KEY, user);
        if (!cancelled) setSession({ status: 'signedIn', user });
      } catch (error) {
        const rejected = error instanceof ApiError && error.status === 401;
        if (rejected) await Promise.all([tokenStore.clear(), removePreference(USER_KEY)]);
        if (!cancelled && (rejected || !remembered)) setSession({ status: 'signedOut' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => onUnauthorized(() => void signOut()), [signOut]);

  const begin = useCallback(async ({ token, user }: AuthResponse) => {
    await Promise.all([tokenStore.save(token), savePreference(USER_KEY, user)]);
    setSession({ status: 'signedIn', user });
  }, []);

  const signIn = useCallback(
    async (credentials: LoginRequest) => begin(await authApi.login(credentials)),
    [begin],
  );

  const signUp = useCallback(
    async (details: RegisterRequest) => begin(await authApi.register(details)),
    [begin],
  );

  const value = useMemo(
    () => ({ session, signIn, signUp, signOut }),
    [session, signIn, signUp, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
